import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Gem, Mail, ShieldCheck, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import SEO from "@/components/SEO";

/**
 * Forgot Password (Phase 16 rewrite).
 *
 * The user enters their account email, clicks "Send reset link", and we
 * always show the SAME neutral confirmation regardless of whether the
 * account exists. This is a hard requirement — never reveal whether an
 * email is registered.
 *
 * The manual "email support to reset" flow (previous UX) has been fully
 * removed; support@postmoro.com is still available for real support
 * needs but is no longer part of the reset path.
 */
export default function ForgotPasswordPage() {
  const [params] = useSearchParams();
  // Carry-forward from /login so the user doesn't retype.
  const carriedEmail = (params.get("email") || "").trim();
  const [email, setEmail] = useState(carriedEmail);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const emailValid = useMemo(() => {
    const v = (email || "").trim();
    return v.length > 3 && /.+@.+\..+/.test(v);
  }, [email]);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!emailValid || loading) return;
    setLoading(true);
    try {
      // Backend ALWAYS returns 200 with a neutral message — no need to
      // branch on the response body. We still await it so the button
      // spinner accurately reflects the request.
      await api.post("/auth/forgot-password", { email: email.trim() });
    } catch (_err) {
      // Even a transport-level error should show the neutral state,
      // otherwise an attacker could distinguish "server down for me
      // right now" from "email exists / doesn't exist". Only network-
      // dead errors would land here and they should self-heal.
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div
      className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] relative overflow-hidden"
      data-testid="forgot-password-page"
    >
      <SEO
        title="Forgot password | Postmoro"
        description="Recover access to your Postmoro account."
        path="/forgot-password"
        noindex
      />
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <header className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] flex items-center justify-center">
            <Gem className="h-4 w-4" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">Postmoro</span>
        </Link>
      </header>

      <main className="relative max-w-[460px] mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="rounded-2xl bg-[color:var(--surface-1)] border border-[color:var(--border-subtle)] shadow-[var(--shadow-card)] p-6 sm:p-7">
          {submitted ? (
            <div data-testid="forgot-password-success">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-9 w-9 rounded-md bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)] flex items-center justify-center shrink-0">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div>
                  <h1
                    className="font-display text-xl font-semibold tracking-tight"
                    data-testid="forgot-password-success-title"
                  >
                    Check your email
                  </h1>
                  <p className="text-sm text-[color:var(--text-2)] mt-1.5 leading-relaxed">
                    If an account exists for that email, we&apos;ve sent a
                    password reset link.
                  </p>
                </div>
              </div>

              <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 mb-4 text-xs text-[color:var(--text-3)] leading-relaxed">
                Didn&apos;t receive anything? Check your spam folder, make
                sure the email was entered correctly, or{" "}
                <Link
                  to="/signup"
                  className="text-[color:var(--brand-primary)] hover:underline"
                  data-testid="forgot-password-signup-link"
                >
                  create an account
                </Link>{" "}
                if you haven&apos;t signed up yet.
              </div>

              <Button
                asChild
                className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="forgot-password-back-login"
              >
                <Link to="/login">
                  <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to login
                </Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="forgot-password-form">
              <div className="flex items-start gap-3 mb-5">
                <div className="h-9 w-9 rounded-md bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)] flex items-center justify-center shrink-0">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div>
                  <h1 className="font-display text-xl font-semibold tracking-tight">
                    Forgot your password?
                  </h1>
                  <p className="text-sm text-[color:var(--text-2)] mt-1.5 leading-relaxed">
                    Enter the email on your Postmoro account and we&apos;ll
                    send you a link to reset your password.
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="forgot-email"
                  className="text-sm font-medium text-[color:var(--text-1)]"
                >
                  Email
                </label>
                <div className="relative mt-1.5">
                  <Mail className="h-4 w-4 text-[color:var(--text-3)] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)]"
                    data-testid="forgot-password-email-input"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={!emailValid || loading}
                className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…
                  </>
                ) : (
                  <>Send reset link</>
                )}
              </Button>

              <div className="mt-4 text-center">
                <Link
                  to="/login"
                  className="text-sm text-[color:var(--text-3)] hover:text-[color:var(--text-1)] inline-flex items-center gap-1"
                  data-testid="forgot-password-cancel"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to login
                </Link>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
